import { useState } from 'react';
import { Stack, Button, Group } from '@mantine/core';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import {
    FormikTextInput,
    PlatformFieldset,
    FormWithButtons
} from '@flexinet/ui-components';
import { BillingProvidersDropdown } from "../../billing-providers/inputs/BillingProvidersDropdown.tsx";
import { BillingProviderPropertyCard } from './BillingProviderPropertyCard';
import { Product } from '../types';
import { useProduct } from '../hooks/useProduct';

interface BillingProviderManagementProps {
    product: Product;
    onProductUpdate?: (updatedProduct: Product) => void;
}

const BillingProviderSchema = Yup.object().shape({
    billingProviderId: Yup.string().optional(),
    billingProviderRecordId: Yup.string().optional(),
});

export const BillingProviderManagement = ({ product, onProductUpdate }: BillingProviderManagementProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(product);
    const { updateRecord } = useProduct(product.id);

    const handleSubmit = async (values: { billingProviderId?: string; billingProviderRecordId?: string }) => {
        try {
            const updatedProduct = {
                ...currentProduct,
                billingProviderId: values.billingProviderId,
                billingProviderRecordId: values.billingProviderRecordId
            };
            
            updateRecord(updatedProduct);
            setCurrentProduct(updatedProduct);
            if (onProductUpdate) {
                onProductUpdate(updatedProduct);
            }
            setIsEditing(false);
        } catch (error) {
            console.error('Failed to update product billing provider:', error);
        }
    };

    const handleRecordUpdate = (updates: Partial<Product>) => {
        const updated = { ...currentProduct, ...updates };
        setCurrentProduct(updated);
        if (onProductUpdate) {
            onProductUpdate(updated);
        }
    };

    if (isEditing) {
        return (
            <Formik
                initialValues={{
                    billingProviderId: currentProduct.billingProviderId || '',
                    billingProviderRecordId: currentProduct.billingProviderRecordId || ''
                }}
                validationSchema={BillingProviderSchema}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, isSubmitting }) => (
                    <FormWithButtons 
                        handleSubmit={handleSubmit} 
                        isSubmitting={isSubmitting}
                        handleCancel={() => setIsEditing(false)}
                        buttonsPosition="bottom"
                    >
                        <PlatformFieldset legend="Billing Provider">
                            <Field
                                name="billingProviderId"
                                label="Billing Provider"
                                component={BillingProvidersDropdown}
                                my="xl"
                            />
                            <Field
                                name="billingProviderRecordId"
                                label="Billing Provider Record ID"
                                description="Please enter the billing provider record ID"
                                component={FormikTextInput}
                                my="xl"
                            />
                        </PlatformFieldset>
                    </FormWithButtons>
                )}
            </Formik>
        );
    }

    return (
        <Stack gap="md">
            <Group justify="flex-end">
                <Button size="xs" variant="light" onClick={() => setIsEditing(true)}>
                    Edit Billing Provider
                </Button>
            </Group>
            <BillingProviderPropertyCard 
                record={currentProduct} 
                onRecordUpdate={handleRecordUpdate}
            />
        </Stack>
    );
};